import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { mergeQueryOptions, retryOnTransient } from 'src/api/utils/helpers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { CustomQueryOptions, FetchError } from 'src/types/TApiQueries';
import { getConnectedApps } from '../functions/connectedAppsApi';
import type { ConnectedAppsListResponse, ConnectedAppsScope } from 'src/types/ConnectedApps';

export function useConnectedAppsQuery(
  scope: ConnectedAppsScope,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ConnectedAppsListResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions<ConnectedAppsListResponse>(options, queryOptions);
  const accessToken = useAccessToken();
  const userId = scope.kind === 'user' ? scope.userId : undefined;

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getConnectedApps(accessToken, scope)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, scope.kind, userId],
  );

  return useQuery<ConnectedAppsListResponse, FetchError>(
    [queryNames.connectedApps, scope.kind, userId],
    queryFn,
    // A 403 (not an admin) or 404 (another account) is an answer, not a blip.
    { retry: retryOnTransient, ...actualQueryOptions, enabled: !!accessToken },
  );
}
